import { ChevronLeft, PencilLine } from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "@/lib/utils";
import { Input } from "antd";
import { Switch } from "@headlessui/react";
import { ParameterTag } from "./parameter-tag";
import { TableTag } from "./table-tag";
import { CreateDetail } from "./create-autopilot";

interface Blueprint {
  id: number;
  name: string;
  color: string;
  icon: React.ReactNode;
}

interface AutomationDetailSidebarProps {
  blueprint: Blueprint;
  isEnabled: boolean;
  lastTriggered: string;
  parameterUsed: string[];
  usedInProfile: string[];
  handleStatusChange: (value: boolean) => void;
}

interface EditAutomationHeaderProps {
  name: string;
  handleBack: () => void;
  navigate: (link: string) => void;
  onEdit: () => void;
  isEdit: boolean
}

export const ViewAutomationHeader = ({ handleBack, onEdit, name, isEdit }: EditAutomationHeaderProps) => (
  <div className="flex justify-between items-center pb-3 border-b border-white-blue px-4 mx-[-16px]">
    <Button
      variant="ghost"
      size="sm"
      className="text-dark-grey"
      onClick={handleBack}
    >
      <ChevronLeft className="h-4 w-4 mr-1" />
      <h2 className="heading-6 text-dark-blue">{name}</h2>
    </Button>
    <div className="flex gap-2">
      <Button
        variant="default"
        size="md"
        className="bg-primary text-white"
        onClick={onEdit}
      >
        <PencilLine className="h-4 w-4 mr-1.5" />
        <p className="button-s">{isEdit ? 'Save' : 'Edit'}</p>
      </Button>
    </div>
  </div>
);

export const AutomationDetailSidebar = ({ 
  blueprint, 
  isEnabled, 
  lastTriggered, 
  parameterUsed, 
  usedInProfile, 
  handleStatusChange 
}: AutomationDetailSidebarProps) => (
  <div className="flex flex-col w-[340px] bg-bg p-6 overflow-y-auto gap-4 bg-light-grey border-r border-white-blue">
    <p className="text-dark-grey heading-7">Automation Details</p>
    <div className="flex w-full justify-between">
      <p className="paragraph-11 text-dark-grey">Status</p>
      <div className="flex gap-1">
        <p className="paragraph-10 text-primary">On</p>
        <Switch
          checked={isEnabled}
          onChange={handleStatusChange}
          className={cn(
            "relative inline-flex h-4 w-8 items-center rounded-full",
            isEnabled ? "bg-alto-blue" : "bg-white-01"
          )}
        >
          <span
            className={cn(
              "inline-block h-3 w-3 transform rounded-full bg-white transition",
              isEnabled ? "translate-x-4" : "translate-x-1"
            )}
          />
        </Switch>
      </div>
    </div>
    <div className="flex w-full justify-between">
      <p className="paragraph-11 text-dark-grey">Last Triggered</p>
      <p className="paragraph-11 text-black">{lastTriggered}</p>
    </div>
    <div className="flex w-full justify-between">
      <p className="paragraph-11 text-dark-grey">Blueprint</p>
      {blueprint && (
        <div className="flex items-center gap-2">
          <div className={cn("w-4 h-4 rounded flex items-center justify-center", blueprint.color)}>
            {blueprint.icon}
          </div>
          <span className="text-black paragraph-12">{blueprint.name}</span>
        </div>
      )}
    </div>
    <div className="flex flex-col w-full gap-1.5">
      <p className="paragraph-11 text-dark-grey">Parameters Used</p>
      <div className="flex gap-1.5 flex-wrap">
        {parameterUsed?.map((param, index) => (
          <ParameterTag key={index} parameter={param} />
        ))}
      </div>
    </div>
    <div className="flex flex-col w-full gap-1.5">
      <p className="paragraph-11 text-dark-grey">Used in Profiles</p>
      <div className="flex gap-1.5 flex-wrap">
        {usedInProfile?.map((profile, index) => (
          <TableTag key={index} text={profile} className="bg-cold" />
        ))}
      </div>
    </div>
  </div>
);

interface AutomationDetailMainProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  blueprint: Blueprint;
  disabled: boolean;
  selectedBlueprint: any;
  constraints: any[];
  setConstraints: (constraints: any[]) => void;
  availableDevices: any[];
  parameters: any[];
  states: any[];
  setStates: (states: any[]) => void;
  interval: number[];
  setInterval: (interval: number[]) => void;
  automationName: string;
  setAutomationName: (name: string) => void;
}

export const AutomationDetailMain = ({
  activeTab,
  setActiveTab,
  blueprint,
  disabled,
  selectedBlueprint,
  constraints,
  setConstraints,
  availableDevices,
  parameters,
  states,
  setStates,
  interval,
  setInterval,
  automationName,
  setAutomationName,
}: AutomationDetailMainProps) => (
  <div className="flex flex-col w-full bg-bg p-6 overflow-y-auto gap-4">
    <div className="flex justify-between">
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <p className="paragraph-14 font-semibold text-dark-blue">
            Automation Name
          </p>
          <input
            type="text"
            value={automationName}
            onChange={(e) => setAutomationName(e.target.value)}
            disabled={disabled}
            placeholder="Automation name"
            className="w-[330px] paragraph-10 max-w-md h-9 px-3 rounded-md border border-light-grey focus:border-primary focus:outline-none disabled:bg-gray-100"
          />
        </div>
        {blueprint?.id === 1 && (
          <div className="flex items-center gap-3">
            <p className="paragraph-14 font-semibold text-dark-blue">
              Set Interval
            </p>
            <Input
              type="number"
              suffix="minutes"
              disabled={disabled}
              value={interval[0]}
              onChange={(e) => setInterval([Number(e.target.value), 0])}
              className="w-32 paragraph-10 h-9 px-3 rounded-md border border-light-grey focus:border-primary focus:outline-none"
            />
          </div>
        )}
      </div>
      {blueprint?.id === 1 && (
        <div>
          <div className="flex gap-3">
            <button
              onClick={() => setActiveTab('states')}
              className={cn(
                "px-[1px] py-1 paragraph-12 border-b-2 transition-colors",
                activeTab === 'states'
                  ? "border-primary text-primary"
                  : "border-white-01 text-dark-grey"
              )}
            >
              States Setting
            </button>
            <button
              onClick={() => setActiveTab('constraints')}
              className={cn(
                "px-[1px] py-1 paragraph-12 border-b-2 transition-colors",
                activeTab === 'constraints'
                  ? "border-primary text-primary"
                  : "border-white-01 text-dark-grey"
              )}
            >
              Constraints Setting
            </button>
          </div>
        </div>
      )}
    </div>
    <CreateDetail 
      activeTab={activeTab}
      states={states}
      setStates={setStates}
      constraints={constraints}
      setConstraints={setConstraints}
      parameters={parameters}
      selectedBlueprint={selectedBlueprint}
      availableDevices={availableDevices}
      editMode={true}
      disabled={disabled}
    />
  </div>
);